import React, {useCallback, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import {specialOptionsForRelateToRiskDropdown, riskFactorKnowledgeTag} from '../../../../../../../../util/uiconstants';
import {getRisksSlice} from '../../../../../../../../selectors/ISA315/selectors';
import SelectDropdown from '../../../../../../SelectDropdown/SelectDropdown';
import EllipsesControl from '../../../../../../EllipsesControl/EllipsesControl';
import {useOverrideLabels} from '../../../../../../../Main/Forms/Utilities/OverrideLabelsProvider';
import InlineMessage from '../../../../../../BaseComponentWrappers/MotifInlineMessage';

export default function RelatedRisksDropdown({
	selectedRiskId,
	isDisabled,
	riskType,
	setRiskType,
	setDisabled,
	setShowRationaleText,
	onRiskTypeChange,
	onRiskChange,
	isCleared,
	setIsCleared,
	knowledgeTagIds
}) {
	const risksById = useSelector((state) => getRisksSlice(state)?.byId);

	const {labels, currentResource} = useOverrideLabels();

	const identifyRiskTag = useCallback((riskTag) => {
		return knowledgeTagIds.find((tag) => {
			return tag === riskTag;
		});
	}, []);

	const identifyOptionToBeDisable = useCallback((riskTypeOptionId) => {
		if (!knowledgeTagIds || knowledgeTagIds?.length === 0) {
			return false;
		}
		if (
			identifyRiskTag(riskFactorKnowledgeTag.FRAUDRISK) &&
			identifyRiskTag(riskFactorKnowledgeTag.SIGNIFICANTRISK) &&
			(riskTypeOptionId === specialOptionsForRelateToRiskDropdown.rommSignificantRisks ||
				riskTypeOptionId === specialOptionsForRelateToRiskDropdown.romm)
		) {
			return true;
		}
		if (
			knowledgeTagIds?.length === 1 &&
			identifyRiskTag(riskFactorKnowledgeTag.FRAUDRISK) &&
			(riskTypeOptionId === specialOptionsForRelateToRiskDropdown.rommSignificantRisks ||
				riskTypeOptionId === specialOptionsForRelateToRiskDropdown.romm)
		) {
			return true;
		}
		return (
			knowledgeTagIds?.length === 1 &&
			identifyRiskTag(riskFactorKnowledgeTag.SIGNIFICANTRISK) &&
			riskTypeOptionId === specialOptionsForRelateToRiskDropdown.romm
		);
	}, []);

	const buildRiskTypeOption = useCallback(
		(riskType) => {
			let riskTypeOption;
			if (riskType > 0) {
				riskTypeOption = {
					value: riskType,
					label: currentResource['riskTypes'].find((rt) => rt.id === riskType)?.title,
					isNotMaterial: false,
					isDisabled: identifyOptionToBeDisable(riskType)
				};
			} else if (selectedRiskId === specialOptionsForRelateToRiskDropdown.notAROMM) {
				riskTypeOption = {
					value: specialOptionsForRelateToRiskDropdown.notAROMM,
					label: labels.notAROMM,
					isNotMaterial: true
				};
			}
			return riskTypeOption;
		},
		[selectedRiskId, labels, currentResource, identifyOptionToBeDisable]
	);

	const [selectedRiskType, setSelectedRiskType] = useState(
		risksById && buildRiskTypeOption(risksById[selectedRiskId]?.riskTypeId)
	);

	useEffect(() => {
		if (riskType) {
			setSelectedRiskType(buildRiskTypeOption(riskType));
		}
	}, [riskType, buildRiskTypeOption]);

	const handleOnRiskChange = useCallback((option) => {
		setIsCleared(false);
		onRiskTypeChange && onRiskTypeChange(option);
		if (option.value === specialOptionsForRelateToRiskDropdown.notAROMM) {
			onRiskChange && onRiskChange(option);
		}
		setSelectedRiskType(buildRiskTypeOption(option.value));
		setRiskType(option.value);
		setDisabled(false);
		setShowRationaleText(option.isNotMaterial);
	}, []);

	const getDropdownOptions = useCallback(() => {
		let riskOptions = [];

		for (const riskType of currentResource.riskTypes) {
			riskOptions.push({
				label: riskType.name,
				value: riskType.id,
				isDisabled: identifyOptionToBeDisable(riskType.id)
			});
		}

		// Add extra "not a romm" option
		riskOptions.push({
			label: labels.notAROMM,
			value: specialOptionsForRelateToRiskDropdown.notAROMM,
			isNotMaterial: true
		});
		return riskOptions;
	}, [risksById]);

	const formatOptionLabel = useCallback((option) => {
		return (
			<section
				//If it's NOT A ROMM, it should be bold
				className={`assessRiskOptionText ${option.label === labels.noRisksIdentified ? 'disabled-option' : ''}`}
			>
				{option?.isDisabled ? (
					<section
						className={'tooltipCustomWidth ROMMRelateToRiskDropdownTooltip'}
						title={
							knowledgeTagIds?.length === 1 && identifyRiskTag(riskFactorKnowledgeTag.SIGNIFICANTRISK)
								? labels.significantRiskTagMessage
								: labels.fraudRiskTagMessage
						}
					>
						{option.label}
					</section>
				) : (
					<EllipsesControl
						content={option.label}
						tooltip={option.label}
						noOfLines={1}
						tooltipClass="tooltipCustomWidth"
					/>
				)}
			</section>
		);
	}, []);

	const label = (isCleared && selectedRiskType) || selectedRiskId === specialOptionsForRelateToRiskDropdown.notAROMM;
	const warningMessage = isCleared || (!selectedRiskType && !riskType);

	const checkValidRiskType = useCallback(() => {
		if (!selectedRiskType || selectedRiskType?.value === specialOptionsForRelateToRiskDropdown.notAROMM) {
			return false;
		}
		if (identifyRiskTag(riskFactorKnowledgeTag.FRAUDRISK) && identifyRiskTag(riskFactorKnowledgeTag.SIGNIFICANTRISK)) {
			selectedRiskType?.value !== specialOptionsForRelateToRiskDropdown.rommFraudRisks;
		}

		if (identifyRiskTag(riskFactorKnowledgeTag.FRAUDRISK)) {
			return selectedRiskType?.value !== specialOptionsForRelateToRiskDropdown.rommFraudRisks;
		}
		if (identifyRiskTag(riskFactorKnowledgeTag.SIGNIFICANTRISK)) {
			return (
				selectedRiskType?.value !== specialOptionsForRelateToRiskDropdown.rommSignificantRisks &&
				selectedRiskType?.value !== specialOptionsForRelateToRiskDropdown.rommFraudRisks
			);
		}

		return false;
	}, [selectedRiskType]);

	return (
		<>
			<StyledRelatedRisksDropdown
				showWarningIcon
				blurInputOnSelect
				isDisabled={isDisabled}
				formLabel={labels.riskType}
				isSearchable
				id="risks"
				noOptionsMessage={() => labels.noResultsFound}
				formatOptionLabel={formatOptionLabel}
				isClearable={false}
				defaultValue={selectedRiskType}
				value={isCleared ? null : selectedRiskType}
				options={getDropdownOptions()}
				isOptionDisabled={(option) => option.isDisabled}
				onChange={handleOnRiskChange}
				keepFormLabelOnBlur={label}
			/>
			{warningMessage && (
				<InlineMessage
					className={'inline-message'}
					variant="warning"
					children={<span className="error-message">{labels.notDefined}</span>}
				/>
			)}
			{!warningMessage && checkValidRiskType() && (
				<InlineMessage
					className={'inline-message'}
					variant="warning"
					children={<span className="error-message">{labels.selectValidRiskType}</span>}
				/>
			)}
		</>
	);
}

RelatedRisksDropdown.propTypes = {
	selectedRiskId: PropTypes.number,
	isDisabled: PropTypes.bool,
	onRiskChange: PropTypes.func,
	onNewRiskClick: PropTypes.func
};

const StyledRelatedRisksDropdown = styled(SelectDropdown)`
	.react-select-container
		.react-select__menu
		.react-select__menu-list
		.react-select__group
		.react-select__group-heading {
		padding-top: var(--px-12);
		padding-bottom: var(--px-12);
	}
	.newrisk-btn {
		padding: var(--px-12);
		border-top: var(--px-1) solid var(--neutrals-700);
		background-color: var(--neutrals-00white);
	}
	.react-select__menu .nota-romm {
		font-size: var(--px-14);
		font-weight: bold;
		padding-top: var(--px-4);
		padding-bottom: var(--px-4);
	}
	.react-select__menu {
		.react-select__option {
			cursor: default;
			padding: 0;
			.assessRiskOptionText {
				cursor: pointer;
				padding: 0;
				&.disabled-option {
					cursor: default;
				}
				.motif-tooltip-wrapper {
					width: 100%;
					.motif-tooltip-trigger-wrap {
						width: 100%;
					}
				}
			}
		}
	}
`;
